import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArchiveTrayIcon, CaretDown, CaretRight, Info, User, FileText, Tag, Vault } from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { useT } from '@/i18n/LanguageContext';
import { plannerApi } from '../plannerApi';

export const CONF_BADGE = {
  high: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  low: 'bg-zinc-100 text-zinc-700 border-zinc-200',
  unmatched: 'bg-zinc-50 text-zinc-400 border-zinc-200',
};
export const STATUS_BADGE = {
  pending_review: 'bg-amber-50 text-amber-700 border-amber-200',
  approved_link: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected_link: 'bg-rose-50 text-rose-600 border-rose-200',
  needs_more_info: 'bg-sky-50 text-sky-700 border-sky-200',
  old_project_created: 'bg-violet-50 text-violet-700 border-violet-200',
};

export function ArchivedContextCard({ rec, t, showStatus = false }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-amber-200/70 bg-amber-50/40 p-3" data-testid={`archived-context-card-${rec.id}`}>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-sm font-semibold text-zinc-800">{rec.archived_project_name}</span>
        <Badge variant="outline" className="bg-white text-zinc-500 border-zinc-200 text-[10px] px-1.5 py-0">{rec.source_system}</Badge>
        <Badge variant="outline" className="bg-white text-indigo-600 border-indigo-200 text-[10px] px-1.5 py-0">{t(`planner.archive.rel.${rec.relationship_type}`)}</Badge>
        <Badge variant="outline" className={`${CONF_BADGE[rec.match_confidence]} text-[10px] px-1.5 py-0`}>{t('planner.archive.confidence')}: {t(`planner.archive.conf.${rec.match_confidence}`)}</Badge>
        {showStatus && <Badge variant="outline" className={`${STATUS_BADGE[rec.review_status]} text-[10px] px-1.5 py-0`}>{t(`planner.archive.status.${rec.review_status}`)}</Badge>}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1.5 text-[11px] text-zinc-500">
        {rec.source_article_id && <span className="inline-flex items-center gap-1"><FileText size={12} /> {t('planner.archive.sourceArticle')} #{rec.source_article_id}</span>}
        <span className="inline-flex items-center gap-1"><ArchiveTrayIcon size={12} /> {rec.source_file_name}</span>
        {(rec.archived_date_start || rec.log_date) && <span>{rec.log_date || rec.archived_date_start}{rec.archived_date_end ? `〜${rec.archived_date_end}` : ''}</span>}
        {(rec.contributors || []).length > 0 && <span className="inline-flex items-center gap-1"><User size={12} /> {rec.contributors.join('、')}</span>}
      </div>
      {rec.summary && <p className="text-xs text-zinc-600 mt-1.5 leading-relaxed">{rec.summary}</p>}
      {(rec.equipment_keywords || []).concat(rec.technical_keywords || []).length > 0 && (
        <div className="flex flex-wrap items-center gap-1 mt-1.5">
          <Tag size={11} className="text-zinc-400" />
          {(rec.equipment_keywords || []).concat(rec.technical_keywords || []).slice(0, 8).map((k) => (
            <span key={k} className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-zinc-200 text-zinc-500">{k}</span>
          ))}
        </div>
      )}
      {rec.match_reason && <p className="text-[11px] text-zinc-400 mt-1">{t('planner.archive.matchReason')}: {rec.match_reason}</p>}
      {rec.raw_excerpt && (
        <div className="mt-1.5">
          <button type="button" onClick={() => setOpen((v) => !v)} data-testid={`archived-context-excerpt-toggle-${rec.id}`}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 hover:text-indigo-700">
            {open ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />} {t('planner.archive.excerpt')}
          </button>
          {open && <pre className="mt-1 p-2 rounded bg-white border border-zinc-200 text-[11px] text-zinc-600 whitespace-pre-wrap max-h-60 overflow-y-auto">{rec.raw_excerpt}</pre>}
        </div>
      )}
    </div>
  );
}

export default function ArchivedContextSection({ projectId }) {
  const { t } = useT();
  const [data, setData] = useState(null);

  useEffect(() => {
    plannerApi.archivedContext(projectId).then(setData).catch(() => setData(null));
  }, [projectId]);

  if (!data) return null;
  const records = data.records || [];
  const oldProjects = data.old_projects || [];
  if (records.length === 0 && oldProjects.length === 0 && !(data.can_review && data.pending_count > 0)) return null;

  return (
    <div className="rounded-xl border border-amber-200 bg-white shadow-sm overflow-hidden" data-testid="archived-context-section">
      <div className="px-4 py-3 bg-amber-50/70 border-b border-amber-200">
        <div className="flex flex-wrap items-center gap-2">
          <ArchiveTrayIcon size={18} weight="duotone" className="text-amber-600" />
          <h2 className="text-sm font-bold text-zinc-800">{t('planner.archive.title')}</h2>
          <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 text-[10px] px-1.5 py-0" data-testid="archived-context-warning-badge">
            <Info size={11} className="mr-0.5" /> {t('planner.archive.referenceOnly')}
          </Badge>
        </div>
        <p className="text-[11px] text-zinc-500 mt-0.5">{t('planner.archive.subtitle')}</p>
      </div>
      <div className="p-3 space-y-2">
        {records.length === 0 && oldProjects.length === 0 && <p className="text-xs text-zinc-400 text-center py-2" data-testid="archived-context-empty">{t('planner.archive.noRecords')}</p>}
        {records.map((rec) => <ArchivedContextCard key={rec.id} rec={rec} t={t} />)}
        {oldProjects.length > 0 && (
          <div className="pt-1 space-y-1.5" data-testid="archived-old-projects-block">
            <p className="text-[11px] font-bold text-zinc-500 uppercase tracking-wide">{t('planner.oldproj.title')}</p>
            {oldProjects.map((p) => (
              <Link key={p.id} to={`/planner/old-projects?open=${p.id}`} data-testid={`archived-oldproj-link-${p.id}`}
                className="flex flex-wrap items-center gap-2 rounded-lg border border-violet-200/70 bg-violet-50/40 px-3 py-2 hover:bg-violet-50 transition-colors">
                <Vault size={14} weight="duotone" className="text-violet-600 shrink-0" />
                <span className="text-xs font-semibold text-zinc-800">{p.name}</span>
                {(p.date_start || p.date_end) && <span className="text-[11px] text-zinc-500">{p.date_start || '?'}〜{p.date_end || ''}</span>}
                {p.summary && <span className="text-[11px] text-zinc-500 truncate max-w-full">{p.summary.slice(0, 80)}</span>}
              </Link>
            ))}
          </div>
        )}
        {data.can_review && data.pending_count > 0 && (
          <Link to="/planner/archive-review" data-testid="archived-context-pending-link"
            className="block text-center text-[11px] font-medium text-indigo-600 hover:text-indigo-700 pt-1">
            {t('planner.archive.pendingLink', { n: data.pending_count })}
          </Link>
        )}
      </div>
    </div>
  );
}
